import type { paths } from "../api-types.js";
import { api } from "../client.js";

/**
 * Signing in, signing up and signing out.
 */

export type Me =
  paths["/api/auth/me"]["get"]["responses"][200]["content"]["application/json"];

export async function fetchMe(): Promise<Me | null> {
  const { data, response } = await api.GET("/api/auth/me");
  if (response.status === 401) return null;
  if (data === undefined) {
    throw new Error(`session check failed with ${String(response.status)}`);
  }
  return data;
}

export async function requestLoginLink(email: string): Promise<void> {
  const { response } = await api.POST("/api/auth/login-link", {
    body: { email: email.trim() },
  });

  if (response.status === 429) {
    throw new Error("Too many attempts. Wait a few minutes and try again.");
  }
  if (!response.ok) {
    throw new Error(`could not send the link (${String(response.status)})`);
  }
}

export async function completeLogin(token: string): Promise<void> {
  const { response } = await api.POST("/api/auth/callback", { body: { token } });
  if (!response.ok) {
    throw new Error("That link is no longer valid. Ask for a new one.");
  }
}

export async function signOut(): Promise<void> {
  const { response } = await api.POST("/api/auth/sign-out");
  if (!response.ok) {
    throw new Error(`sign out failed with ${String(response.status)}`);
  }
}

export async function signIn(email: string, password: string): Promise<void> {
  // Trimmed: an address pasted with a trailing space was refused as malformed
  // and shown as "do not match", which sent people hunting for a wrong
  // password that was right all along (2026-10-03).
  const { response } = await api.POST("/api/auth/sign-in", {
    body: { email: email.trim(), password },
  });

  if (response.ok) return;
  if (response.status === 429) {
    throw new Error("Too many attempts. Wait 15 minutes and try again.");
  }
  if (response.status === 400) {
    throw new Error(
      "Check the email address, and that the password has at least 12 characters.",
    );
  }
  if (response.status === 401) {
    // Deliberately one message for the account and the password. Saying which
    // half was wrong would tell an anonymous caller whether the account exists.
    throw new Error(
      "That email and password do not match. If you are not sure of the password, use “Forgot your password?”.",
    );
  }
  throw new Error("Crux could not sign you in just now. Try again in a minute.");
}

/** Emails a link to choose a new password. The same answer for any address. */
export async function requestPasswordReset(email: string): Promise<void> {
  const { response } = await api.POST("/api/auth/password-reset-link", {
    body: { email: email.trim() },
  });
  if (response.status === 429) {
    throw new Error("Too many attempts. Wait 15 minutes and try again.");
  }
  if (!response.ok) {
    throw new Error(`could not send the link (${String(response.status)})`);
  }
}

/** Sets a new password from an emailed link; the session cookie comes back. */
export async function resetPassword(token: string, password: string): Promise<void> {
  const { response } = await api.POST("/api/auth/password-reset", {
    body: { token, password },
  });
  if (response.ok) return;
  if (response.status === 400) {
    throw new Error("Use at least 12 characters.");
  }
  if (response.status === 429) {
    throw new Error("Too many attempts. Wait 15 minutes and try again.");
  }
  throw new Error(
    "That link is no longer valid. Ask for a new one from the sign-in page.",
  );
}
